import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search, Trash2, X, Printer, Edit } from 'lucide-react';
import { toast } from 'sonner';

// Delivery Challan (DC) — captured for Tally integration. Holds Customer Name,
// Company Details, DC Number, DC Date and the dispatched material lines.
const blankLine = () => ({ material_name: '', quantity: '' });

const DeliveryChallan = () => {
  const [challans, setChallans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const emptyForm = { dc_number: '', dc_date: '', customer_name: '', company_details: '', notes: '', items: [blankLine()] };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dRes, cRes, sRes] = await Promise.all([
        api.get('/tally/delivery-challans'),
        api.get('/purchase/customers'),
        api.get('/sales/company-settings')
      ]);
      setChallans(dRes.data); setCustomers(cRes.data); setCompanySettings(sRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditingId(null); setFormData({ ...emptyForm, items: [blankLine()] }); setShowModal(true); };
  const openEdit = (dc) => {
    setEditingId(dc.id);
    setFormData({
      dc_number: dc.dc_number || '', dc_date: dc.dc_date || '',
      customer_name: dc.customer_name || '', company_details: dc.company_details || '',
      notes: dc.notes || '', items: (dc.items && dc.items.length > 0) ? dc.items.map(i => ({ ...i })) : [blankLine()]
    });
    setShowModal(true);
  };

  const addLine = () => setFormData(prev => ({ ...prev, items: [...prev.items, blankLine()] }));
  const removeLine = (idx) => setFormData(prev => prev.items.length > 1 ? ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }) : prev);
  const updateLine = (idx, field, val) => setFormData(prev => {
    const items = [...prev.items]; items[idx] = { ...items[idx], [field]: val }; return { ...prev, items };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = formData.items.filter(i => (i.material_name || '').trim() || (i.quantity || '').toString().trim());
    try {
      if (editingId) {
        await api.put(`/tally/delivery-challans/${editingId}`, { ...formData, items });
        toast.success('Delivery challan updated');
      } else {
        await api.post('/tally/delivery-challans', { ...formData, items });
        toast.success('Delivery challan created');
      }
      setShowModal(false); setEditingId(null); setFormData({ ...emptyForm }); fetchData();
    } catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to manage delivery challans' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  const deleteDC = async (id, number) => {
    if (!window.confirm(`Delete delivery challan "${number}"?`)) return;
    try { await api.delete(`/tally/delivery-challans/${id}`); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const printDC = (dc) => {
    const co = companySettings || {};
    const rows = (dc.items || []).map((it, i) =>
      `<tr><td style="text-align:center">${i + 1}</td><td>${it.material_name || ''}</td><td style="text-align:right">${it.quantity || ''}</td></tr>`
    ).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Delivery Challan - ${dc.dc_number}</title>
      <style>
        @page{size:A4;margin:15mm}body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:18px}
        .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #000;padding-bottom:10px;margin-bottom:10px}
        .company-info{text-align:right;font-size:10px;line-height:1.5}
        .doc-title{text-align:center;font-size:18px;font-weight:bold;margin:10px 0;text-decoration:underline}
        .meta{display:flex;justify-content:space-between;margin:8px 0}
        .box{border:1px solid #bbb;padding:8px;font-size:11px;margin:8px 0;white-space:pre-line}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th,td{border:1px solid #999;padding:6px 8px;font-size:11px}
        th{background:#1a237e;color:#fff}
        .footer{display:flex;justify-content:space-between;margin-top:50px}
        .footer div{border-top:1px solid #000;padding-top:4px;width:180px;text-align:center;font-size:11px}
        @media print{button{display:none !important}}
      </style></head><body>
      <div class="header">
        <div><img src="https://hridya.dweb.co.in/hridayalogo.png" alt="logo" style="height:60px; width:auto;" /></div>
        <div class="company-info">${co.address || ''}<br/>${co.email || ''}<br/>${co.website || ''}</div>
      </div>
      <div class="doc-title">DELIVERY CHALLAN</div>
      <div class="meta"><div><strong>DC No:</strong> ${dc.dc_number}</div><div><strong>DC Date:</strong> ${dc.dc_date || ''}</div></div>
      <div class="box"><strong>Customer:</strong> ${dc.customer_name || ''}${dc.company_details ? `\n${dc.company_details}` : ''}</div>
      <table><thead><tr><th style="width:50px">#</th><th>Material Name / Description</th><th style="width:120px">Quantity</th></tr></thead>
      <tbody>${rows}</tbody></table>
      ${dc.notes ? `<p style="margin-top:12px"><strong>Notes:</strong> ${dc.notes}</p>` : ''}
      <div class="footer"><div>Prepared by</div><div>Authorised Signatory</div></div>
      <button onclick="window.print()" style="margin-top:24px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const filtered = challans.filter(d =>
    (d.dc_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="delivery-challan-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Delivery Challan</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-dc-btn"><Plus size={16} /> Create Delivery Challan</button>
      </div>

      <div className="card mb-4">
        <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search by DC #, customer..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading...</div> : filtered.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>No delivery challans yet.</div>
        ) : (
          <div className="table-container">
            <table>
              <thead><tr><th>DC #</th><th>DC Date</th><th>Customer</th><th>Items</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(dc => (
                  <tr key={dc.id} data-testid={`dc-row-${dc.dc_number}`}>
                    <td className="font-mono">{dc.dc_number}</td>
                    <td className="text-sm">{dc.dc_date || '-'}</td>
                    <td>{dc.customer_name}</td>
                    <td>{dc.items?.length || 0} items</td>
                    <td className="text-sm">{new Date(dc.created_at).toLocaleDateString('en-GB')}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(dc)} className="btn btn-ghost btn-sm" title="Edit"><Edit size={14} /></button>
                        <button onClick={() => printDC(dc)} className="btn btn-ghost btn-sm" title="Print"><Printer size={14} /></button>
                        <button onClick={() => deleteDC(dc.id, dc.dc_number)} className="btn btn-ghost btn-sm" title="Delete"><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="dc-modal">
          <div className="modal-content" style={{ maxWidth: '720px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editingId ? 'Edit Delivery Challan' : 'Create Delivery Challan'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">DC Number</label>
                    <input type="text" className="form-input" value={formData.dc_number} onChange={(e) => setFormData({ ...formData, dc_number: e.target.value })} placeholder="Auto-generated if blank" data-testid="dc-number" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">DC Date</label>
                    <input type="text" className="form-input" value={formData.dc_date} onChange={(e) => setFormData({ ...formData, dc_date: e.target.value })} placeholder="DD/MM/YYYY" data-testid="dc-date" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Customer Name *</label>
                    <input type="text" list="dc-customers" className="form-input" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} required data-testid="dc-customer" />
                    <datalist id="dc-customers">{customers.map(c => <option key={c.id} value={c.name} />)}</datalist>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company Details</label>
                    <textarea className="form-input" rows={2} value={formData.company_details} onChange={(e) => setFormData({ ...formData, company_details: e.target.value })} placeholder="Address / GSTIN / contact" data-testid="dc-company" />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '16px 0 8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Materials</h3>
                  <button type="button" onClick={addLine} className="btn btn-secondary btn-sm"><Plus size={12} /> Add Material</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px' }}>
                  <thead><tr><th style={{ padding: 6, textAlign: 'left' }}>Material Name / Description</th><th style={{ padding: 6, textAlign: 'left', width: 160 }}>Quantity</th><th style={{ width: 32 }}></th></tr></thead>
                  <tbody>
                    {formData.items.map((line, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: 4 }}><input type="text" className="form-input" value={line.material_name} onChange={(e) => updateLine(idx, 'material_name', e.target.value)} style={{ fontSize: 12, padding: 4 }} /></td>
                        <td style={{ padding: 4 }}><input type="text" className="form-input" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} placeholder="e.g. 500 kg / 1200 nos" style={{ fontSize: 12, padding: 4 }} /></td>
                        <td style={{ padding: 4 }}>{formData.items.length > 1 && <button type="button" onClick={() => removeLine(idx)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowModal(false); setEditingId(null); }} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-dc-btn">{editingId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryChallan;
